import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowUpRight, Globe, ShoppingBag, Wifi } from 'lucide-react';

const HERO_BG = 'https://images.pexels.com/photos/18435276/pexels-photo-18435276.jpeg?auto=compress&w=1920';

export function EcosystemPage() {
  const { i18n } = useTranslation();
  
  const getText = (fr, ar, en) => {
    if (i18n.language === 'fr') return fr;
    if (i18n.language === 'ar') return ar;
    return en;
  };

  const entities = [
    {
      name: 'epaas.io',
      tagline: i18n.language === 'fr' ? 'eProduct as a Service' : i18n.language === 'ar' ? 'المنتجات الإلكترونية كخدمة' : 'eProduct as a Service',
      desc: i18n.language === 'fr' 
        ? 'Distribution unifiée et simplifiée de produits digitaux sur le web, e-commerce, POS et bornes. Vendez vos eProducts partout en quelques minutes.'
        : i18n.language === 'ar'
        ? 'توزيع موحد ومبسط للمنتجات الرقمية عبر الويب والتجارة الإلكترونية ونقاط البيع والأكشاك.'
        : 'Unified & Simplified Distribution of Digital Products across web, e-commerce, POS, and kiosks. Sell eProducts everywhere in minutes.',
      icon: ShoppingBag,
      url: 'https://epaas.io',
      screenshot: '/assets/epaas.png',
      features: [
        i18n.language === 'fr' ? 'Web, Plugin, POS, Kiosk' : i18n.language === 'ar' ? 'ويب، إضافة، نقطة بيع، كشك' : 'Web, Plugin, POS, Kiosk',
        i18n.language === 'fr' ? 'Actif dans 11 pays' : i18n.language === 'ar' ? 'نشط في 11 دولة' : 'Live in 11 countries',
        i18n.language === 'fr' ? 'Gagnez dès la première vente' : i18n.language === 'ar' ? 'اربح من أول عملية بيع' : 'Earn from first sale'
      ]
    },
    {
      name: 'interfracom.com',
      tagline: i18n.language === 'fr' ? 'Plateforme de distribution Demat' : i18n.language === 'ar' ? 'منصة توزيع رقمية' : 'Demat Distribution Platform',
      desc: i18n.language === 'fr'
        ? 'Solutions innovantes pour votre business. Plateforme de distribution dématérialisée pour la vente de produits et services numériques.'
        : i18n.language === 'ar'
        ? 'حلول مبتكرة لأعمالك. منصة توزيع رقمية لبيع المنتجات والخدمات الرقمية.'
        : 'Innovative solutions for your business. Dematerialized distribution platform for selling digital products and services.',
      icon: Wifi,
      url: 'https://interfracom.com',
      screenshot: '/assets/interfracom.png',
      features: [
        i18n.language === 'fr' ? 'Distribution dématérialisée' : i18n.language === 'ar' ? 'توزيع رقمي' : 'Dematerialized distribution',
        i18n.language === 'fr' ? 'Solutions retail' : i18n.language === 'ar' ? 'حلول التجزئة' : 'Retail solutions',
        i18n.language === 'fr' ? 'Accompagnement personnalisé' : i18n.language === 'ar' ? 'دعم مخصص' : 'Personalized support'
      ]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <div data-testid="ecosystem-page">
      {/* Hero Section with background */}
      <section className="relative min-h-[70vh] flex items-center" data-testid="ecosystem-hero">
        <div className="absolute inset-0 z-0">
          <img src={HERO_BG} alt="Ecosystem" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
        </div>
        
        <div className="container-main relative z-10 py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="text-label text-gold mb-6">
              {i18n.language === 'fr' ? 'ÉCOSYSTÈME D.ONE' : i18n.language === 'ar' ? 'نظام دي.ون البيئي' : 'D.ONE ECOSYSTEM'}
            </p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[0.95] mb-8">
              {i18n.language === 'fr' ? (
                <>
                  Nos<br/>
                  <span className="text-gold">entités</span>
                </>
              ) : i18n.language === 'ar' ? (
                <>
                  <span className="text-gold">كياناتنا</span>
                </>
              ) : (
                <>
                  Our<br/>
                  <span className="text-gold">entities</span>
                </>
              )}
            </h1>
            <p className="text-lg text-white/70 max-w-2xl">
              {i18n.language === 'fr'
                ? 'Un écosystème de solutions complémentaires pour répondre à tous vos besoins télécom et retail.'
                : i18n.language === 'ar'
                ? 'نظام بيئي من الحلول التكميلية لتلبية جميع احتياجاتك في مجال الاتصالات والتجزئة.'
                : 'An ecosystem of complementary solutions to meet all your telecom and retail needs.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Entities Section */}
      <section className="section-dark section-xl" data-testid="entities-section">
        <div className="container-main">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="space-y-12"
          >
            {entities.map((entity, index) => (
              <motion.div
                key={entity.name}
                variants={itemVariants}
                className="grid lg:grid-cols-2 gap-12 items-center py-12 border-b border-white/10 last:border-0"
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 rounded-full bg-gold flex items-center justify-center">
                      <entity.icon className="w-6 h-6 text-black" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gold">{entity.name}</h2>
                      <p className="text-white/60 text-sm">{entity.tagline}</p>
                    </div>
                  </div>
                  <p className="text-white/80 text-body-lg mb-8">{entity.desc}</p>
                  <ul className="space-y-3 mb-8">
                    {entity.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-3 text-white/60">
                        <div className="w-1.5 h-1.5 rounded-full bg-gold" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <a
                    href={entity.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-outline-light"
                  >
                    {i18n.language === 'fr' ? 'Visiter le site' : i18n.language === 'ar' ? 'زيارة الموقع' : 'Visit website'}
                    <ArrowUpRight className="w-4 h-4" />
                  </a>
                </div>
                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <a 
                    href={entity.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block overflow-hidden rounded-xl bg-gradient-to-br from-white/10 to-white/5 border border-white/20 hover:border-[#f4b00f]/50 transition-all group"
                  >
                    <div className="relative overflow-hidden">
                      <img 
                        src={entity.screenshot} 
                        alt={entity.name}
                        className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0">
                        <div className="text-[#f4b00f] text-sm font-medium flex items-center gap-2">
                          {getText('Visiter le site', 'زيارة الموقع', 'Visit website')}
                          <ArrowUpRight className="w-4 h-4" />
                        </div>
                      </div>
                    </div>
                  </a>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Synergies Section */}
      <section className="section-cream section-xl" data-testid="synergies-section">
        <div className="container-main text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-label text-gold mb-4">
              {i18n.language === 'fr' ? 'SYNERGIES' : i18n.language === 'ar' ? 'التآزر' : 'SYNERGIES'}
            </p>
            <h2 className="text-display mb-8">
              {i18n.language === 'fr' ? (
                <>Un groupe, <span className="text-gold">une vision</span></>
              ) : i18n.language === 'ar' ? (
                <>مجموعة واحدة، <span className="text-gold">رؤية واحدة</span></>
              ) : (
                <>One group, <span className="text-gold">one vision</span></>
              )}
            </h2>
            <p className="text-body-lg text-muted max-w-2xl mx-auto">
              {i18n.language === 'fr'
                ? 'Nos entités travaillent en synergie pour offrir des solutions complètes et intégrées, de la conception à la mise en marché.'
                : i18n.language === 'ar'
                ? 'تعمل كياناتنا بشكل تآزري لتقديم حلول كاملة ومتكاملة، من التصميم إلى طرح السوق.'
                : 'Our entities work in synergy to offer complete and integrated solutions, from design to market launch.'}
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default EcosystemPage;
